import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { cn } from '~/lib/cn';

type AccordionFrontendProps = {
    id: string;
    title: string;
    subtitle?: string;
    children?: React.ReactNode;
    color?: string;
    isOpen: boolean;
    onToggle: () => void;
};

export const AccordionFrontend: React.FC<React.PropsWithChildren<React.PropsWithChildren<AccordionFrontendProps>>> = ({
    id,
    title,
    subtitle,
    children,
    color,
    isOpen,
    onToggle
}) => {
    return (
        <div className="tw-card-bordered p-0 tw-rounded-xl" style={color ? { backgroundColor: color } : undefined}>
            <button
                type="button"
                onClick={onToggle}
                className="tw-flex tw-w-full tw-items-start tw-justify-between tw-gap-4 tw-border-none tw-bg-transparent tw-p-4 tw-text-left"
                aria-expanded={isOpen}
                aria-controls={id}
            >
                <div>
                    <p className="tw-mb-2 tw-text-base tw-font-semibold tw-text-accents-9">{title}</p>
                    {subtitle && <p className="tw-mb-0 tw-text-sm tw-text-accents-8">{subtitle}</p>}
                </div>
                <motion.span
                    className="tw-text-accents-7"
                    animate={{ rotate: isOpen ? 0 : 90 }}
                    transition={{ duration: 0.15 }}
                >
                    <svg width="30" height="30" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.085l3.71-3.855a.75.75 0 1 1 1.08 1.04l-4.24 4.4a.75.75 0 0 1-1.08 0l-4.24-4.4a.75.75 0 0 1 .02-1.06z" />
                    </svg>
                </motion.span>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        id={id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="px-4 tw-mt-2 tw-overflow-hidden tw-text-sm tw-text-accents-7"
                    >
                        <div className="[&>p]:tw-mb-0">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export interface AccordionProps {
    title: string | React.ReactNode;
    /** @deprecated */
    bordered?: boolean;
    subtitle?: string;
    color?: string;
    children?: React.ReactNode;
    defaultOpen?: boolean;
    className?: string;
    triggerClassName?: string;
    titleClassName?: string;
    iconClassName?: string;
    contentClassName?: string;
    contentInnerClassName?: string;
    isOpen?: boolean;
    onToggle?: () => void;
    /**
     * Drop the card around the panel, for a list of sections separated by hairlines
     * rather than a stack of boxes. The trigger, the animation and the aria wiring
     * are unchanged — only the chrome goes.
     */
    flat?: boolean;
    /**
     * Keeps the contents mounted while collapsed, hidden rather than removed.
     *
     * For a panel whose children do something on mount — register form fields,
     * fetch, detect, seed a default. Unmounted, all of that happens the first
     * time somebody expands the panel, so opening it *changes the page*: the
     * booking import's field mapping guessed a wish column on mount, which moved
     * "Wunschpartner 0/12" to "3/12" as a consequence of clicking a disclosure
     * arrow. A number that moves when you look at it is not a number anybody can
     * act on.
     *
     * Off by default: for the ordinary case — text, a table, a form the club has
     * to open anyway — not building the contents until they are asked for is the
     * cheaper and better behaviour.
     */
    keepMounted?: boolean;
}

export const Accordion: React.FC<React.PropsWithChildren<React.PropsWithChildren<AccordionProps>>> = ({
    title,
    bordered,
    color,
    children,
    defaultOpen = false,
    className,
    triggerClassName,
    titleClassName,
    iconClassName,
    contentClassName,
    contentInnerClassName,
    isOpen,
    onToggle,
    flat = false,
    keepMounted = false
}) => {
    const [internalIsOpen, setInternalIsOpen] = React.useState(defaultOpen);
    const contentId = uuid();
    const isControlled = typeof isOpen === 'boolean';
    const currentIsOpen = isControlled ? Boolean(isOpen) : internalIsOpen;

    const toggle = React.useCallback(() => {
        if (!isControlled) {
            setInternalIsOpen((prev) => !prev);
        }
        onToggle?.();
    }, [isControlled, onToggle]);

    return (
        <div
            className={cn(flat ? 'tw-w-full' : 'tw-card-bordered tw-mb-2 tw-rounded-xl tw-p-0', className)}
            style={color ? { backgroundColor: color } : undefined}
        >
            <button
                type="button"
                onClick={toggle}
                className={cn(
                    'tw-flex tw-w-full tw-items-start tw-justify-between tw-gap-4 tw-border-none tw-bg-transparent tw-p-4 tw-text-left',
                    triggerClassName
                )}
                aria-expanded={currentIsOpen}
                aria-controls={contentId}
            >
                <div className={cn('tw-flex tw-flex-1 tw-flex-col tw-text-lg tw-text-accents-9', titleClassName)}>
                    {title}
                </div>

                <motion.span
                    className={cn('tw-text-accents-7', iconClassName)}
                    animate={{ rotate: currentIsOpen ? 0 : 90 }}
                    transition={{ duration: 0.2 }}
                >
                    <svg width="30" height="30" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.085l3.71-3.855a.75.75 0 1 1 1.08 1.04l-4.24 4.4a.75.75 0 0 1-1.08 0l-4.24-4.4a.75.75 0 0 1 .02-1.06z" />
                    </svg>
                </motion.span>
            </button>

            {keepMounted ? (
                // One element in one position, collapsed to nothing rather than
                // taken out: React keeps the subtree alive exactly as long as
                // this stays the same node. `inert` is what makes a zero-height
                // box genuinely absent — height and overflow only hide it, and a
                // collapsed panel whose selects still take Tab is worse than one
                // that is simply not there.
                <motion.div
                    id={contentId}
                    initial={false}
                    animate={{ height: currentIsOpen ? 'auto' : 0, opacity: currentIsOpen ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                    inert={!currentIsOpen}
                    className={cn(
                        'tw-overflow-hidden tw-px-4 tw-text-sm tw-text-accents-7',
                        currentIsOpen && 'tw-mt-2 tw-pb-3',
                        contentClassName
                    )}
                >
                    <div className={cn('[&>p]:tw-mb-0', contentInnerClassName)}>{children}</div>
                </motion.div>
            ) : (
                <AnimatePresence initial={false}>
                    {currentIsOpen && (
                        <motion.div
                            id={contentId}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className={cn(
                                'tw-mt-2 tw-overflow-hidden tw-px-4 tw-pb-3 tw-text-sm tw-text-accents-7',
                                contentClassName
                            )}
                        >
                            <div className={cn('[&>p]:tw-mb-0', contentInnerClassName)}>{children}</div>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
};
